import type {
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";
import { t } from "ttag";

import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { useScrollToTop } from "metabase/common/hooks";
import { MonitorEmptyState } from "metabase/monitor/components/MonitorEmptyState";
import { MonitorTableCard } from "metabase/monitor/components/MonitorTableCard";
import { listChannelSummaries } from "metabase/monitor/tools/notifications/utils";
import type { TreeTableColumnDef } from "metabase/ui";
import {
  Badge,
  Card,
  Ellipsified,
  Flex,
  Icon,
  LoadingOverlay,
  Text,
  Tooltip,
  TreeTable,
  TreeTableSkeleton,
  useTreeTableInstance,
} from "metabase/ui";
import { EMPTY_CELL_PLACEHOLDER } from "metabase/utils/constants";
import { getUserLabel } from "metabase/utils/user";
import type { AdminNotification, NotificationId } from "metabase-types/api";

import { NotificationSummary } from "../NotificationSummary";
import {
  getChannelIconName,
  getChannelLabel,
} from "../NotificationsAdminPage/utils";

type Props = {
  notifications: AdminNotification[];
  error: unknown;
  isFetching: boolean;
  isLoading: boolean;
  page: number;
  rowSelection: RowSelectionState;
  selectedDetailId: NotificationId | undefined;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onRowClick?: (id: NotificationId) => void;
};

const getNodeId = (notification: AdminNotification) => String(notification.id);

export const NotificationsTable = ({
  notifications,
  error,
  isFetching,
  isLoading,
  page,
  rowSelection,
  selectedDetailId,
  sorting,
  onSortingChange,
  onRowSelectionChange,
  onRowClick,
}: Props) => {
  const selectedRowId =
    selectedDetailId !== undefined ? String(selectedDetailId) : null;

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(next);
    },
    [sorting, onSortingChange],
  );

  const columns = useMemo<TreeTableColumnDef<AdminNotification>[]>(
    () => [
      {
        id: "id",
        header: t`ID`,
        width: 80,
        enableSorting: true,
        accessorFn: (notification) => notification.id,
        cell: ({ row }) => (
          <Flex justify="center">
            <Badge variant="light" color="brand" size="xs" miw={29}>
              {row.original.id}
            </Badge>
          </Flex>
        ),
      },
      {
        id: "card_name",
        header: t`Question`,
        minWidth: 200,
        enableSorting: true,
        accessorFn: (notification) => notification.payload?.card?.name ?? "",
        cell: ({ row }) => {
          const card = row.original.payload?.card;
          const cardId = row.original.payload?.card_id;
          const name = card?.name ?? (cardId ? `#${cardId}` : t`Unknown`);
          return <Ellipsified tooltip={name}>{name}</Ellipsified>;
        },
      },
      {
        id: "creator_name",
        header: t`Owner`,
        width: "auto",
        minWidth: 140,
        maxAutoWidth: 320,
        enableSorting: true,
        accessorFn: (notification) =>
          notification.creator?.common_name ??
          notification.creator?.email ??
          "",
        cell: ({ row }) => {
          const creator = row.original.creator;
          const name = getUserLabel(creator);
          const isDeactivated = creator?.is_active === false;
          return (
            <Flex gap="xs" align="center" miw={0}>
              <Ellipsified tooltip={name}>{name}</Ellipsified>
              {isDeactivated && (
                <Tooltip label={t`Deactivated owner`}>
                  <Icon name="ghost" size={16} c="text-secondary" />
                </Tooltip>
              )}
            </Flex>
          );
        },
      },
      {
        id: "channel",
        header: t`Channel`,
        width: "auto",
        enableSorting: false,
        accessorFn: (notification) =>
          listChannelSummaries(notification)
            .map(({ channel, count }) => `${channel}${count}`)
            .join(" "),
        cell: ({ row }) => {
          const summaries = listChannelSummaries(row.original);
          if (summaries.length === 0) {
            return EMPTY_CELL_PLACEHOLDER;
          }
          return (
            <Flex gap="sm" align="center" wrap="wrap">
              {summaries.map(({ channel, count }) => (
                <Tooltip key={channel} label={getChannelLabel(channel)}>
                  <Flex gap="xs" align="center">
                    <Icon
                      name={getChannelIconName(channel)}
                      c="text-secondary"
                      size={16}
                    />
                    <Text size="md" c="text-primary">
                      {count}
                    </Text>
                  </Flex>
                </Tooltip>
              ))}
            </Flex>
          );
        },
      },
      {
        id: "last_check",
        header: t`Last checked`,
        width: 170,
        enableSorting: true,
        sortDescFirst: true,
        accessorFn: (notification) => notification.last_check?.at ?? "",
        cell: ({ row }) => (
          <NotificationSummary run={row.original.last_check} isCompact={true} />
        ),
      },
      {
        id: "last_send",
        header: t`Last send attempt`,
        width: 200,
        enableSorting: true,
        sortDescFirst: true,
        accessorFn: (notification) => notification.last_send?.at ?? "",
        cell: ({ row }) => (
          <NotificationSummary run={row.original.last_send} isCompact={true} />
        ),
      },
    ],
    [],
  );

  const handleRowActivate = useCallback(
    (row: Row<AdminNotification>) => {
      onRowClick?.(row.original.id);
    },
    [onRowClick],
  );

  const instance = useTreeTableInstance<AdminNotification>({
    data: notifications,
    columns,
    getNodeId,
    sorting,
    manualSorting: true,
    enableRowSelection: true,
    rowSelection,
    onRowSelectionChange,
    onRowActivate: handleRowActivate,
    onSortingChange: handleSortingChange,
    selectedRowId,
  });

  const { setActiveRowId } = instance;
  useEffect(() => {
    setActiveRowId(selectedRowId);
  }, [selectedRowId, setActiveRowId]);

  useScrollToTop({
    ref: instance.containerRef,
    keys: [page, sorting],
    skip: isFetching,
  });

  const getRowProps = useCallback(
    (row: Row<AdminNotification>) => ({
      "data-testid": `notification-row-${row.original.id}`,
    }),
    [],
  );

  if (error !== undefined) {
    return (
      <Card
        flex="0 1 auto"
        mih={0}
        withBorder
        p="lg"
        data-testid="notifications-admin-table"
      >
        <LoadingAndErrorWrapper error={error} />
      </Card>
    );
  }

  return (
    <MonitorTableCard
      aria-busy={isFetching}
      data-testid="notifications-admin-table"
    >
      {isLoading ? (
        <TreeTableSkeleton
          showCheckboxes
          columnWidths={[0.06, 0.28, 0.18, 0.16, 0.16, 0.16]}
        />
      ) : (
        <>
          <LoadingOverlay visible={isFetching} data-testid="loading-overlay" />
          <TreeTable
            instance={instance}
            hierarchical={false}
            showCheckboxes
            onHeaderCheckboxClick={() => instance.table.toggleAllRowsSelected()}
            headerCheckboxAriaLabel={t`Select all`}
            ariaLabel={t`Notifications`}
            onRowClick={handleRowActivate}
            getRowProps={getRowProps}
            emptyState={<MonitorEmptyState label={t`No alerts`} />}
          />
        </>
      )}
    </MonitorTableCard>
  );
};
